/* eslint-disable eag/match-named-export -- Pre-existing grab-bag file with several exports */
import {
  RecordAuditDefinitionSection,
  RecordAuditDefinitionTask,
} from '../types/RecordAuditDefinitionFormValue';

/**
 * Task order inside the form is carried purely by array order (no position field on
 * the backend). All operations below keep one invariant: the flat task array is always
 * grouped in display order — unsectioned tasks first (they are shown first when filling
 * the audit), then tasks of each section in section order. Thanks to that, an array
 * index is also a display position, which keeps drag & drop index math trivial.
 */
export const orderTasksForDisplay = (
  tasks: RecordAuditDefinitionTask[],
  sections: RecordAuditDefinitionSection[]
): RecordAuditDefinitionTask[] => {
  const knownSectionIds = new Set(sections.map((section) => section.id));
  const groups = new Map<string | null, RecordAuditDefinitionTask[]>([
    [null, []],
    ...sections.map((section) => [section.id, []] as [string | null, RecordAuditDefinitionTask[]]),
  ]);

  tasks.forEach((task) => {
    const groupKey =
      task.sectionId !== null && knownSectionIds.has(task.sectionId) ? task.sectionId : null;
    groups.get(groupKey)?.push(groupKey === task.sectionId ? task : {...task, sectionId: null});
  });

  return [...groups.values()].flat();
};

export const addTaskToSection = (
  tasks: RecordAuditDefinitionTask[],
  sections: RecordAuditDefinitionSection[],
  sectionId: string | null,
  newTask: RecordAuditDefinitionTask
): RecordAuditDefinitionTask[] =>
  orderTasksForDisplay([...tasks, {...newTask, sectionId}], sections);

export const duplicateTaskAt = (
  tasks: RecordAuditDefinitionTask[],
  index: number
): RecordAuditDefinitionTask[] => {
  const original = tasks[index];
  if (!original) {
    return tasks;
  }
  const copy = {...original, id: undefined};
  return [...tasks.slice(0, index + 1), copy, ...tasks.slice(index + 1)];
};

export const moveTaskWithinSection = (
  tasks: RecordAuditDefinitionTask[],
  index: number,
  direction: -1 | 1
): RecordAuditDefinitionTask[] => {
  const task = tasks[index];
  if (!task) {
    return tasks;
  }
  const groupIndexes = tasks
    .map((candidate, candidateIndex) => ({candidate, candidateIndex}))
    .filter(({candidate}) => candidate.sectionId === task.sectionId)
    .map(({candidateIndex}) => candidateIndex);
  const positionInGroup = groupIndexes.indexOf(index);
  const neighborIndex = groupIndexes[positionInGroup + direction];
  if (neighborIndex === undefined) {
    return tasks;
  }
  const next = [...tasks];
  next[index] = tasks[neighborIndex];
  next[neighborIndex] = task;
  return next;
};

export const moveTaskToSection = (
  tasks: RecordAuditDefinitionTask[],
  sections: RecordAuditDefinitionSection[],
  index: number,
  sectionId: string | null
): RecordAuditDefinitionTask[] => {
  const task = tasks[index];
  if (!task) {
    return tasks;
  }
  const rest = tasks.filter((_, candidateIndex) => candidateIndex !== index);
  // Appending to the array end and re-normalizing places the task at the end of the
  // target group while keeping every other task's relative order (stable grouping).
  return orderTasksForDisplay([...rest, {...task, sectionId}], sections);
};

export const removeSectionWithTasks = (
  tasks: RecordAuditDefinitionTask[],
  sectionId: string
): RecordAuditDefinitionTask[] => tasks.filter((task) => task.sectionId !== sectionId);
